import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Step,
  StepLabel,
  Stepper,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  Stop,
  Visibility,
  TrendingUp,
  Schedule,
  Group,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface Campaign {
  id: number;
  name: string;
  status: 'active' | 'paused' | 'scheduled' | 'completed' | 'draft';
  progress: number;
  budget: string;
  startDate: string;
  endDate: string;
  channels: string[];
  performance: {
    impressions: string;
    clicks: string;
    conversions: string;
    ctr: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type ChannelSelectionOption = '' | 'meta' | 'google' | 'both';

type StepKey = 'base' | 'meta' | 'google';

interface MetaAudienceState {
  ageMin: string;
  ageMax: string;
  genders: {
    male: boolean;
    female: boolean;
    diverse: boolean;
  };
  interests: string;
  behaviors: string;
  demographics: string;
}

interface NewCampaignFormState {
  userId: string;
  name: string;
  channelSelection: ChannelSelectionOption;
  budget: string;
  budgetMeta: string;
  budgetGoogle: string;
  metaRegionMode: 'plz' | 'radius';
  metaPostalCodes: string;
  metaRadiusCenter: string;
  metaRadiusKm: string;
  metaAudience: MetaAudienceState;
  googleLocations: string;
  googleSuggestedTitle: string;
  googleSuggestedDescription: string;
  googleSuggestedImage: string;
  startDate: string;
  endDate: string;
}

interface CampaignManager {
  id: string;
  name: string;
}

type MetaAudienceValueKey = Exclude<keyof MetaAudienceState, 'genders'>;

interface StepDefinition {
  key: StepKey;
  label: string;
}

const createDefaultMetaAudience = (): MetaAudienceState => ({
  ageMin: '18',
  ageMax: '65',
  genders: {
    male: true,
    female: true,
    diverse: false,
  },
  interests: '',
  behaviors: '',
  demographics: '',
});

const createDefaultNewCampaignState = (): NewCampaignFormState => ({
  userId: '',
  name: '',
  channelSelection: '',
  budget: '',
  budgetMeta: '',
  budgetGoogle: '',
  metaRegionMode: 'plz',
  metaPostalCodes: '',
  metaRadiusCenter: '',
  metaRadiusKm: '',
  metaAudience: createDefaultMetaAudience(),
  googleLocations: '',
  googleSuggestedTitle: '',
  googleSuggestedDescription: '',
  googleSuggestedImage: '',
  startDate: '',
  endDate: '',
});

const CAMPAIGN_MANAGERS: CampaignManager[] = [
  { id: 'user-anna-schmidt', name: 'Anna Schmidt' },
  { id: 'user-david-meyer', name: 'David Meyer' },
  { id: 'user-lisa-berg', name: 'Lisa Berg' },
];

const generateGoogleTitle = (campaignName: string) =>
  campaignName ? `${campaignName} – jetzt sichtbar werden` : 'Lokale Sichtbarkeit steigern';

const generateGoogleDescription = (campaignName: string) =>
  campaignName
    ? `Erreichen Sie neue Kund:innen mit ${campaignName} – optimiert für Ihre Region.`
    : 'Erreichen Sie neue Kund:innen mit einer optimierten, lokalen Google-Kampagne.';

const generateGoogleImage = (campaignName: string) => {
  const label = campaignName || 'Google Kampagne';
  return `https://placehold.co/600x400?text=${encodeURIComponent(label)}`;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const CampaignManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [newCampaignStep, setNewCampaignStep] = useState(0);
  const [newCampaignData, setNewCampaignData] = useState<NewCampaignFormState>(createDefaultNewCampaignState);

  const campaigns: Campaign[] = [
    {
      id: 1,
      name: 'Sommer Handwerker Kampagne',
      status: 'active',
      progress: 75,
      budget: '€ 25.000',
      startDate: '01.08.2025',
      endDate: '30.09.2025',
      channels: ['Meta', 'Google'],
      performance: {
        impressions: '1.2M',
        clicks: '45.2K',
        conversions: '2.8K',
        ctr: '3.77%',
      },
    },
    {
      id: 2,
      name: 'Herbst Promotion 2025',
      status: 'scheduled',
      progress: 100,
      budget: '€ 18.500',
      startDate: '01.10.2025',
      endDate: '15.11.2025',
      channels: ['Meta', 'Google'],
      performance: {
        impressions: '0',
        clicks: '0',
        conversions: '0',
        ctr: '0%',
      },
    },
    {
      id: 3,
      name: 'Weihnachts Special',
      status: 'draft',
      progress: 30,
      budget: '€ 35.000',
      startDate: '01.12.2025',
      endDate: '31.12.2025',
      channels: ['Meta'],
      performance: {
        impressions: '0',
        clicks: '0',
        conversions: '0',
        ctr: '0%',
      },
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'var(--brand)';
      case 'scheduled': return 'var(--warning)';
      case 'paused': return 'var(--slate-600)';
      case 'completed': return 'var(--success)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'scheduled': return 'Geplant';
      case 'paused': return 'Pausiert';
      case 'completed': return 'Abgeschlossen';
      default: return 'Entwurf';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaignId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaignId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  const handleNewCampaignOpen = () => {
    setNewCampaignOpen(true);
    setNewCampaignStep(0);
  };

  const resetCampaignForm = () => {
    setNewCampaignStep(0);
    setNewCampaignData(createDefaultNewCampaignState());
  };

  const handleNewCampaignClose = () => {
    setNewCampaignOpen(false);
    resetCampaignForm();
  };

  const handleInputChange = (field: keyof NewCampaignFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewCampaignData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: keyof NewCampaignFormState) => (event: SelectChangeEvent<string>) => {
    setNewCampaignData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleChannelSelectionToggle = (_event: React.MouseEvent<HTMLElement>, value: ChannelSelectionOption) => {
    if (value === null) {
      return;
    }

    setNewCampaignData(prev => ({
      ...prev,
      channelSelection: value,
      budgetMeta: value === 'meta' || value === 'both' ? prev.budgetMeta : '',
      budgetGoogle: value === 'google' || value === 'both' ? prev.budgetGoogle : '',
    }));
  };

  const handleAudienceGenderToggle = (gender: keyof MetaAudienceState['genders']) => () => {
    setNewCampaignData(prev => ({
      ...prev,
      metaAudience: {
        ...prev.metaAudience,
        genders: {
          ...prev.metaAudience.genders,
          [gender]: !prev.metaAudience.genders[gender],
        },
      },
    }));
  };

  const handleMetaAudienceChange = (field: MetaAudienceValueKey) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewCampaignData(prev => ({
      ...prev,
      metaAudience: {
        ...prev.metaAudience,
        [field]: event.target.value,
      },
    }));
  };

  const handleMetaRegionModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'plz' | 'radius';
    setNewCampaignData(prev => ({
      ...prev,
      metaRegionMode: value,
      metaPostalCodes: value === 'plz' ? prev.metaPostalCodes : '',
      metaRadiusCenter: value === 'radius' ? prev.metaRadiusCenter : '',
      metaRadiusKm: value === 'radius' ? prev.metaRadiusKm : '',
    }));
  };

  const handleStepperNext = () => {
    setNewCampaignStep(prev => Math.min(prev + 1, stepDefinitions.length - 1));
  };

  const handleStepperBack = () => {
    setNewCampaignStep(prev => Math.max(prev - 1, 0));
  };

  const selectedChannelStepKeys = useMemo<StepKey[]>(() => {
    const keys: StepKey[] = ['base'];

    if (newCampaignData.channelSelection === 'meta' || newCampaignData.channelSelection === 'both') {
      keys.push('meta');
    }

    if (newCampaignData.channelSelection === 'google' || newCampaignData.channelSelection === 'both') {
      keys.push('google');
    }

    return keys;
  }, [newCampaignData.channelSelection]);

  const stepDefinitions = useMemo<StepDefinition[]>(() => (
    selectedChannelStepKeys.map((key) => {
      switch (key) {
        case 'base':
          return { key, label: 'Grundlagen' };
        case 'meta':
          return { key, label: 'Meta Konfiguration' };
        case 'google':
          return { key, label: 'Google Konfiguration' };
        default:
          return { key, label: key };
      }
    })
  ), [selectedChannelStepKeys]);

  const currentStepKey: StepKey = stepDefinitions[newCampaignStep]?.key ?? 'base';

  const canProceedFromBase = useMemo(() => {
    const baseFieldsFilled =
      newCampaignData.userId.trim().length > 0 &&
      newCampaignData.name.trim().length > 0 &&
      newCampaignData.channelSelection !== '' &&
      newCampaignData.budget.trim().length > 0 &&
      newCampaignData.startDate.trim().length > 0 &&
      newCampaignData.endDate.trim().length > 0;

    if (!baseFieldsFilled) {
      return false;
    }

    if (newCampaignData.channelSelection === 'meta') {
      return newCampaignData.budgetMeta.trim().length > 0;
    }

    if (newCampaignData.channelSelection === 'google') {
      return newCampaignData.budgetGoogle.trim().length > 0;
    }

    if (newCampaignData.channelSelection === 'both') {
      return (
        newCampaignData.budgetMeta.trim().length > 0 &&
        newCampaignData.budgetGoogle.trim().length > 0
      );
    }

    return true;
  }, [
    newCampaignData.budget,
    newCampaignData.budgetGoogle,
    newCampaignData.budgetMeta,
    newCampaignData.channelSelection,
    newCampaignData.endDate,
    newCampaignData.name,
    newCampaignData.startDate,
    newCampaignData.userId,
  ]);

  const canProceedFromMeta = useMemo(() => {
    if (!(newCampaignData.channelSelection === 'meta' || newCampaignData.channelSelection === 'both')) {
      return true;
    }

    if (newCampaignData.metaRegionMode === 'plz') {
      return newCampaignData.metaPostalCodes.trim().length > 0;
    }

    return (
      newCampaignData.metaRadiusCenter.trim().length > 0 &&
      newCampaignData.metaRadiusKm.trim().length > 0
    );
  }, [
    newCampaignData.channelSelection,
    newCampaignData.metaPostalCodes,
    newCampaignData.metaRadiusCenter,
    newCampaignData.metaRadiusKm,
    newCampaignData.metaRegionMode,
  ]);

  const canProceedFromGoogle = useMemo(() => {
    if (!(newCampaignData.channelSelection === 'google' || newCampaignData.channelSelection === 'both')) {
      return true;
    }

    return newCampaignData.googleLocations.trim().length > 0;
  }, [
    newCampaignData.channelSelection,
    newCampaignData.googleLocations,
  ]);

  const canProceed = useMemo(() => {
    switch (currentStepKey) {
      case 'base':
        return canProceedFromBase;
      case 'meta':
        return canProceedFromMeta;
      case 'google':
        return canProceedFromGoogle;
      default:
        return true;
    }
  }, [canProceedFromBase, canProceedFromGoogle, canProceedFromMeta, currentStepKey]);

  const isLastStep = newCampaignStep === stepDefinitions.length - 1;

  useEffect(() => {
    if (stepDefinitions.length === 0) {
      setNewCampaignStep(0);
      return;
    }

    if (newCampaignStep >= stepDefinitions.length) {
      setNewCampaignStep(stepDefinitions.length - 1);
    }
  }, [newCampaignStep, stepDefinitions]);

  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const scheduledCampaigns = campaigns.filter(c => c.status === 'scheduled');
  const allCampaigns = campaigns;

  return (
    <Box>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Kampagnen-Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              Verwalten Sie alle Ihre Marketing-Kampagnen zentral
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<Add />} onClick={handleNewCampaignOpen}>
            Neue Kampagne
          </Button>
        </Box>
      </Box>
      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {activeCampaigns.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktive Kampagnen
                  </Typography>
                </Box>
                <CampaignIcon sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {scheduledCampaigns.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Geplante Kampagnen
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    € 78.5K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamt Budget
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    6.1K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamt Conversions
                  </Typography>
                </Box>
                <Group sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Campaigns Table */}
      <Card sx={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Alle Kampagnen" />
            <Tab label="Aktive Kampagnen" />
            <Tab label="Geplante Kampagnen" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kampagne</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Fortschritt</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Budget</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Laufzeit</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kanäle</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Performance</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Aktionen</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{campaign.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getStatusText(campaign.status)} size="small" sx={{ color: getStatusColor(campaign.status), border: '1px solid var(--line)', backgroundColor: 'var(--slate-50)', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={campaign.progress} sx={{ width: 80, height: 6, borderRadius: 3, backgroundColor: 'var(--slate-100)', '& .MuiLinearProgress-bar': { backgroundColor: 'var(--brand)' } }} />
                        <Typography variant="body2">{campaign.progress}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{campaign.budget}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{campaign.startDate} - {campaign.endDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {campaign.channels.map((channel) => (
                          <Chip key={channel} label={channel} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {campaign.performance.impressions} Impressions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {campaign.performance.clicks} Clicks (CTR: {campaign.performance.ctr})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, campaign.id)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kampagne</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Fortschritt</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Budget</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Performance</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Aktionen</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{campaign.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={campaign.progress} 
                          sx={{ 
                            width: 100, 
                            height: 8, 
                            borderRadius: 3,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: brandColors.electricTeal
                            }
                          }}
                        />
                        <Typography variant="body2">{campaign.progress}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{campaign.budget}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.electricTeal }}>
                        {campaign.performance.conversions} Conversions
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ color: 'var(--brand)' }}>
                          <Visibility />
                        </IconButton>
                        <IconButton size="small" sx={{ color: 'var(--slate-600)' }}>
                          <Pause />
                        </IconButton>
                        <IconButton size="small" sx={{ color: 'var(--danger)' }}>
                          <Stop />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kampagne</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Startdatum</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Budget</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kanäle</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Aktionen</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{campaign.name}</Typography>
                    </TableCell>
                    <TableCell>{campaign.startDate}</TableCell>
                    <TableCell>{campaign.budget}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {campaign.channels.map((channel) => (
                          <Chip key={channel} label={channel} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ color: 'var(--brand)' }}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" sx={{ color: 'var(--brand)' }}>
                          <PlayArrow />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          Anzeigen
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Pause fontSize="small" sx={{ mr: 1 }} />
          Pausieren
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: '#f44336' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      {/* New Campaign Dialog */}
      <Dialog open={newCampaignOpen} onClose={handleNewCampaignClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Neue Kampagne erstellen
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stepper activeStep={newCampaignStep} alternativeLabel sx={{ mb: 3 }}>
            {stepDefinitions.map(step => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {currentStepKey === 'base' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel>User auswählen</InputLabel>
                  <Select
                    value={newCampaignData.userId}
                    onChange={handleSelectChange('userId')}
                    label="User auswählen"
                  >
                    {CAMPAIGN_MANAGERS.map(manager => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {newCampaignData.userId.trim().length === 0 && (
                    <FormHelperText error>Bitte eine verantwortliche Person wählen.</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label="Kampagnen-Name"
                  value={newCampaignData.name}
                  onChange={handleInputChange('name')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label="Gesamtbudget"
                  value={newCampaignData.budget}
                  onChange={handleInputChange('budget')}
                  placeholder="z.B. € 25.000"
                />
              </Grid>
              <Grid size={12}>
                <ToggleButtonGroup
                  value={newCampaignData.channelSelection}
                  exclusive
                  onChange={handleChannelSelectionToggle}
                  fullWidth
                >
                  <ToggleButton value="meta">Meta</ToggleButton>
                  <ToggleButton value="google">Google</ToggleButton>
                  <ToggleButton value="both">Meta & Google</ToggleButton>
                </ToggleButtonGroup>
                {newCampaignData.channelSelection === '' && (
                  <FormHelperText error sx={{ mt: 1 }}>Bitte mindestens einen Kanal wählen.</FormHelperText>
                )}
              </Grid>
              {(newCampaignData.channelSelection === 'meta' || newCampaignData.channelSelection === 'both') && (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Budget für Meta"
                    value={newCampaignData.budgetMeta}
                    onChange={handleInputChange('budgetMeta')}
                    placeholder="z.B. € 12.500"
                  />
                </Grid>
              )}
              {(newCampaignData.channelSelection === 'google' || newCampaignData.channelSelection === 'both') && (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Budget für Google"
                    value={newCampaignData.budgetGoogle}
                    onChange={handleInputChange('budgetGoogle')}
                    placeholder="z.B. € 12.500"
                  />
                </Grid>
              )}
              <Grid size={12}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Startdatum"
                      type="date"
                      value={newCampaignData.startDate}
                      onChange={handleInputChange('startDate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Enddatum"
                      type="date"
                      value={newCampaignData.endDate}
                      onChange={handleInputChange('endDate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {currentStepKey === 'meta' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Region auswählen</FormLabel>
                  <RadioGroup
                    row
                    value={newCampaignData.metaRegionMode}
                    onChange={handleMetaRegionModeChange}
                  >
                    <FormControlLabel value="plz" control={<Radio />} label="Nach Postleitzahl" />
                    <FormControlLabel value="radius" control={<Radio />} label="Radius um einen Ort" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {newCampaignData.metaRegionMode === 'plz' ? (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    label="Postleitzahlen"
                    placeholder="z.B. 10115, 10117, 10119"
                    helperText="Mehrere PLZ mit Komma trennen. Karte zeigt das Zielgebiet an."
                    value={newCampaignData.metaPostalCodes}
                    onChange={handleInputChange('metaPostalCodes')}
                  />
                </Grid>
              ) : (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Zentrum (Adresse oder PLZ)"
                      value={newCampaignData.metaRadiusCenter}
                      onChange={handleInputChange('metaRadiusCenter')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Radius in km"
                      value={newCampaignData.metaRadiusKm}
                      onChange={handleInputChange('metaRadiusKm')}
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <Box sx={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--slate-50)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Karte mit markiertem Zielgebiet (Platzhalter)
                  </Typography>
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Zielgruppe</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Min. Alter"
                  value={newCampaignData.metaAudience.ageMin}
                  onChange={handleMetaAudienceChange('ageMin')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Max. Alter"
                  value={newCampaignData.metaAudience.ageMax}
                  onChange={handleMetaAudienceChange('ageMax')}
                />
              </Grid>
              <Grid size={12}>
                <FormGroup row>
                  <FormControlLabel control={<Checkbox checked={newCampaignData.metaAudience.genders.male} onChange={handleAudienceGenderToggle('male')} />} label="Männlich" />
                  <FormControlLabel control={<Checkbox checked={newCampaignData.metaAudience.genders.female} onChange={handleAudienceGenderToggle('female')} />} label="Weiblich" />
                  <FormControlLabel control={<Checkbox checked={newCampaignData.metaAudience.genders.diverse} onChange={handleAudienceGenderToggle('diverse')} />} label="Divers" />
                </FormGroup>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Interessen"
                  placeholder="z.B. Handwerk, Bau, Modernisierung"
                  value={newCampaignData.metaAudience.interests}
                  onChange={handleMetaAudienceChange('interests')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Verhaltensweisen"
                  placeholder="z.B. Immobilienkäufer:innen, Umzug"
                  value={newCampaignData.metaAudience.behaviors}
                  onChange={handleMetaAudienceChange('behaviors')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Demografische Merkmale"
                  placeholder="z.B. Hausbesitzer:innen, Eltern"
                  value={newCampaignData.metaAudience.demographics}
                  onChange={handleMetaAudienceChange('demographics')}
                />
              </Grid>
            </Grid>
          )}

          {currentStepKey === 'google' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  label="PLZ oder Städte"
                  placeholder="z.B. 80331 München; 22419 Hamburg"
                  helperText="Mehrere Einträge mit Semikolon trennen. Karte visualisiert das Zielgebiet."
                  value={newCampaignData.googleLocations}
                  onChange={handleInputChange('googleLocations')}
                />
              </Grid>
              <Grid size={12}>
                <Box sx={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--slate-50)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Karte mit Google Zielgebiet (Platzhalter)
                  </Typography>
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Anzeige-Vorschläge</Typography>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Titel Vorschlag"
                  value={newCampaignData.googleSuggestedTitle || generateGoogleTitle(newCampaignData.name)}
                  onChange={handleInputChange('googleSuggestedTitle')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Beschreibung Vorschlag"
                  value={newCampaignData.googleSuggestedDescription || generateGoogleDescription(newCampaignData.name)}
                  onChange={handleInputChange('googleSuggestedDescription')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Bild Vorschlag (URL)"
                  value={newCampaignData.googleSuggestedImage || generateGoogleImage(newCampaignData.name)}
                  onChange={handleInputChange('googleSuggestedImage')}
                />
                <Box sx={{ mt: 2, border: '1px solid var(--line)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  <Box component="img" src={newCampaignData.googleSuggestedImage || generateGoogleImage(newCampaignData.name)} alt="Google Vorschlagsbild" sx={{ width: '100%', height: 'auto' }} />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleNewCampaignClose}>Abbrechen</Button>
          {newCampaignStep > 0 && (
            <Button onClick={handleStepperBack} sx={{ mr: 'auto' }}>
              Zurück
            </Button>
          )}
          {!isLastStep && (
            <Button
              variant="contained"
              onClick={handleStepperNext}
              disabled={!canProceed}
            >
              Weiter
            </Button>
          )}
          {isLastStep && (
            <Button
              variant="contained"
              disabled={!canProceed}
              onClick={handleNewCampaignClose}
              sx={{
                bgcolor: brandColors.electricTeal,
                '&:hover': { bgcolor: brandColors.midnightSteel }
              }}
            >
              Kampagne erstellen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignManagement;
